import { useEffect, useId, useRef, useState } from 'react';
import { isActive, keyboardUp, splitTabs, typesText, type NavGroup } from './nav';
import { useSession } from './session';
import type { FloorSummary, SellerSummary } from './types';
import { Icon } from './ui/icons';

/** The account's own pages, opened from the sheet's foot: "Menü" stays lit on them. */
const ACCOUNT = ['/ayarlar', '/guvenlik', '/sifre'];
/** Where the bar shows instead of the rail (styles.css, Package H). */
const PHONE = 'screen and (max-width: 600px)';

export interface TabBarProps {
  /** the menu's blocks, already cut to what the role sees */
  readonly groups: readonly NavGroup[];
  /** the entries that become tabs, most used first */
  readonly preferred: readonly string[];
  readonly path: string;
  readonly summary: (FloorSummary & SellerSummary) | undefined;
  readonly roleLabel: string;
  /** the warehouse, or the seller, this panel is for */
  readonly place: string;
  readonly avatar: string;
  readonly version?: string;
}

/**
 * A phone's menu (≤ 600 px, where the rail is hidden): the four most used screens as tabs along the
 * bottom, everything else the role sees in a full-height sheet behind "Menü". Wider screens never show it.
 */
export function TabBar({ groups, preferred, path, summary, roleLabel, place, avatar, version }: TabBarProps) {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const keyboard = usePhoneKeyboard();
  const sheet = useRef<HTMLDivElement>(null);
  // what had the cursor (the scan field), given back when the sheet closes on the same screen
  const before = useRef<HTMLElement | null>(null);
  const warehouseField = useId();
  const { tabs, rest } = splitTabs(groups, preferred);
  const count = (item: NavGroup['items'][number]) => (summary && item.count ? item.count(summary) : 0);
  const waitingWork = (item: NavGroup['items'][number]) => (summary && item.waiting ? item.waiting(summary) : 0);

  const close = () => {
    setOpen(false);
    const back = before.current;
    before.current = null;
    if (back?.isConnected) back.focus();
  };
  const remember = () => {
    const active = document.activeElement;
    // the scan field, never "Menü" itself: a reader's Enter on it would open the sheet again
    before.current = active instanceof HTMLElement && active !== document.body && !active.closest('.tabbar') ? active : null;
  };
  // a link to the screen already open changes no route: it only closes the sheet
  const follow = (to: string) => (to === path ? close() : setOpen(false));

  // another screen (a link, the phone's back button) closes the sheet
  useEffect(() => {
    setOpen(false);
    before.current = null;
  }, [path]);
  useEffect(() => {
    if (!open) return;
    sheet.current?.focus();
    // the screen underneath hears no keys while it is open (a reader's scan must not confirm a pick
    // behind the sheet); Escape closes it
    const onKey = (event: KeyboardEvent) => {
      event.stopPropagation();
      if (event.key === 'Escape') close();
    };
    // a phone turned sideways gets the rail back: the sheet closes and the scan field takes the cursor again
    const phone = window.matchMedia(PHONE);
    const onTurn = () => !phone.matches && close();
    window.addEventListener('keydown', onKey, true);
    phone.addEventListener('change', onTurn);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      phone.removeEventListener('change', onTurn);
    };
  }, [open]);

  const onTab = tabs.some((item) => isActive(item.path, path));
  const inSheet = rest.some((g) => g.items.some((item) => isActive(item.path, path))) || ACCOUNT.some((p) => isActive(p, path));
  const menuLit = open || (!onTab && inSheet);
  // a queue waiting behind "Menü" (problem orders, a count to approve) shows as a dot on it
  const waiting = rest.some((g) => g.items.some((item) => count(item) > 0));

  return (
    <>
      <nav className={`tabbar${keyboard ? ' away' : ''}`} aria-label="Ana menü">
        {tabs.map((item) => {
          const on = !open && isActive(item.path, path);
          const n = count(item);
          // work waiting there (orders to pick, parcels for the courier): grey, a problem's red count comes first
          const work = n > 0 ? 0 : waitingWork(item);
          return (
            <a
              key={item.path}
              href={`#${item.path}`}
              className={`tab${on ? ' active' : ''}`}
              aria-label={n + work > 0 ? `${item.label}: ${n + work} bekliyor` : item.label}
              aria-current={on ? 'page' : undefined}
              onClick={() => follow(item.path)}
            >
              <span className="tab-icon">
                <Icon name={item.icon} size={22} />
              </span>
              <span className="tab-label">{item.short ?? item.label}</span>
              {n > 0 && <span className="count">{n > 99 ? '99+' : n}</span>}
              {work > 0 && <span className="count work">{work > 99 ? '99+' : work}</span>}
            </a>
          );
        })}
        <button
          type="button"
          className={`tab${menuLit ? ' active' : ''}`}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={waiting ? 'Menü, bekleyen işler var' : 'Menü'}
          // the cursor is still in the scan field when the finger lands
          onPointerDown={() => !open && remember()}
          onClick={() => {
            if (open) return close();
            if (!before.current) remember();
            setOpen(true);
          }}
        >
          <span className="tab-icon">
            <Icon name={open ? 'x' : 'menu'} size={22} />
          </span>
          <span className="tab-label">Menü</span>
          {waiting && <span className="dot" aria-hidden />}
        </button>
      </nav>
      {open && (
        // "modal-back": like any dialog, the scan field underneath leaves the cursor alone while it is open
        <div className="modal-back nav-sheet" role="dialog" aria-modal aria-label="Menü" ref={sheet} tabIndex={-1}>
          <div className="nav-sheet-h">
            <span className="avatar" aria-hidden>
              {avatar}
            </span>
            <span className="who-text">
              <b>{session.me.fullName}</b>
              <small>
                {roleLabel} · {place}
              </small>
            </span>
            <button className="icon-btn" onClick={close} aria-label="Menüyü kapat">
              <Icon name="x" />
            </button>
          </div>
          {session.isOperator && session.warehouses.length > 1 && (
            <div className="field nav-sheet-wh">
              <label htmlFor={warehouseField}>Çalıştığınız depo</label>
              <select id={warehouseField} className="select" value={session.warehouseId ?? ''} onChange={(e) => session.setWarehouseId(e.target.value)}>
                {session.warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} · {w.code}
                  </option>
                ))}
              </select>
            </div>
          )}
          {rest.map((group) => (
            <section key={group.title} className="nav-sheet-group" aria-label={group.title || undefined}>
              {group.title && <h3>{group.title}</h3>}
              {group.items.map((item) => {
                const on = isActive(item.path, path);
                const n = count(item);
                return (
                  <a key={item.path} href={`#${item.path}`} className={`nav-row${on ? ' active' : ''}`} aria-current={on ? 'page' : undefined} onClick={() => follow(item.path)}>
                    <Icon name={item.icon} />
                    <span className="nav-row-label">{item.label}</span>
                    {n > 0 && <span className="count">{n}</span>}
                  </a>
                );
              })}
            </section>
          ))}
          <section className="nav-sheet-group" aria-label="Hesap">
            <h3>Hesap</h3>
            <a className={`nav-row${ACCOUNT.some((p) => isActive(p, path)) ? ' active' : ''}`} href="#/ayarlar" onClick={() => follow('/ayarlar')}>
              <Icon name="sliders" />
              <span className="nav-row-label">
                Ayarlar
                <small>Görünüm, şifre, iki adımlı doğrulama</small>
              </span>
            </a>
            <button type="button" className="nav-row out" onClick={session.logout}>
              <Icon name="logout" />
              <span className="nav-row-label">Çıkış yap</span>
            </button>
          </section>
          <p className="nav-sheet-foot">
            {version && <>Sürüm {version} · </>}
            Pronto Depo, bir Yılmazer Lojistik hizmetidir
          </p>
        </div>
      )}
    </>
  );
}

/**
 * Whether the phone's on-screen keyboard is up (the tab bar then steps aside, not to sit on top of
 * it). A barcode reader typing into the scan field brings no keyboard up, so the bar stays.
 */
function usePhoneKeyboard(): boolean {
  const [up, setUp] = useState(false);
  useEffect(() => {
    const view = window.visualViewport;
    let width = 0;
    let tallest = 0;
    const check = () => {
      // at the page's own scale: a pinch zoom shrinks the visible page as well, only the keyboard counts
      const height = view ? view.height * view.scale : window.innerHeight;
      // turning the phone changes its full height: measured afresh
      if (window.innerWidth !== width) {
        width = window.innerWidth;
        tallest = 0;
      }
      tallest = Math.max(tallest, height);
      setUp(keyboardUp(typesText(document.activeElement), height, tallest));
    };
    check();
    view?.addEventListener('resize', check);
    window.addEventListener('resize', check);
    document.addEventListener('focusin', check);
    document.addEventListener('focusout', check);
    return () => {
      view?.removeEventListener('resize', check);
      window.removeEventListener('resize', check);
      document.removeEventListener('focusin', check);
      document.removeEventListener('focusout', check);
    };
  }, []);
  return up;
}
